import React, { useLayoutEffect, useEffect, useState, useImperativeHandle, forwardRef, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import '../css/news.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock } from '@fortawesome/free-regular-svg-icons';
import { faArrowRight, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import Gallery from './Gallery';
import RecentPosts from './RecentPosts';
import RecentPatchNotes from './RecentPatchNotes';
import LoadingSpinner from './LoadingSpinner';
import i18n from "i18next";

const backgrounds = [
    '/images/news-defaults/gatling.png', //gatling
    '/images/news-defaults/driver.png', //driver
    '/images/news-defaults/firearms_expert.png', //firearms
    '/images/news-defaults/gas_soldier.png', //gas soldier
    '/images/news-defaults/marine.png', //marine
    '/images/news-defaults/nuclear.png', //nuclear
    '/images/news-defaults/seeker.png', //seeker
    '/images/news-defaults/shotgun_master.png', //shotgun master
    '/images/news-defaults/sniper.png', //sniper
    '/images/news-defaults/strike_force.png', //strike force
    '/images/news-defaults/swat.png', //swat
    '/images/news-defaults/teleporter.png', //teleporter
    '/images/news-defaults/titan.png' //titan
];

const News = forwardRef((props, ref) => {
    const [news, setNews] = useState([]);
    const [error, setError] = useState(null);
    const [isNewsLoading, setIsNewsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(5);
    const [totalPages, setTotalPages] = useState(0);
    const newsSectionRef = useRef(null);
    const galleryRef = useRef(null);
    const { t, i18n } = useTranslation();

    const getRandomBackgrounds = () => {
        const shuffledBackgrounds = [...backgrounds].sort(() => 0.5 - Math.random());
        return shuffledBackgrounds.slice(0, news.length);
    };

    const fetchNews = useCallback(() => {
        setIsNewsLoading(true);
        fetch(`${process.env.REACT_APP_API_URL}/api/news?lang=${i18n.language}&page=${page}&limit=${limit}`, {
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (!response.ok) throw new Error('Failed to fetch news');
                return response.json();
            })
            .then((data) => {
                setNews(data.news);
                setTotalPages(Math.ceil(data.total / limit));
                setIsNewsLoading(false);
            })
            .catch((error) => {
                setError(error.message);
                setIsNewsLoading(false);
            });
    }, [i18n.language, page, limit]);

    const scrollToNews = () => {
        if (newsSectionRef.current) {
            newsSectionRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useImperativeHandle(ref, () => ({
        fetchNews,
        scrollToNews,
    }));

    useEffect(() => {
        fetchNews();
    }, [fetchNews]);

    const handlePageClick = (data) => {
        const selectedPage = data.selected + 1;
        setPage(selectedPage);
    };

    if (error) return <div>Error: {error}</div>;

    const randomBackgrounds = getRandomBackgrounds();

    return (
        <section id="news-section" className="latest-news" ref={newsSectionRef}>
            <div className="news-container">
                <div className="section-title">
                    <span className="title-text">{t('news.latestNews') || 'Latest News'}</span>
                    <span className="arrow-icon">›</span>
                </div>
                <div id="news-wrapper">
                    {isNewsLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <aside id="news">
                                {news.map((item, index) => {
                                    const background = item.image ? item.image : randomBackgrounds[index % randomBackgrounds.length];
                                    return (
                                        <NewsItem
                                            key={item.id}
                                            item={item}
                                            background={background}
                                            t={t}
                                        />
                                    );
                                })}
                                {totalPages > 1 && !isNewsLoading && (
                                    <ReactPaginate
                                        breakLabel="..."
                                        nextLabel={null}
                                        onPageChange={handlePageClick}
                                        pageRangeDisplayed={1}
                                        marginPagesDisplayed={2}
                                        pageCount={totalPages}
                                        previousLabel={null}
                                        forcePage={page - 1}
                                        containerClassName="pagination-container"
                                        pageClassName="pagination-item"
                                        pageLinkClassName="pagination-link"
                                        previousClassName="pagination-item"
                                        nextClassName="pagination-next"
                                        previousLinkClassName="pagination-previous"
                                        nextLinkClassName="pagination-link"
                                        breakClassName="pagination-item"
                                        breakLinkClassName="pagination-link"
                                        activeClassName="active"
                                    />
                                )}
                            </aside>
                            <div className="sidebar-wrapper">
                                <RecentPatchNotes />
                                <RecentPosts />
                            </div>
                        </>
                    )}
                </div>
                <Gallery ref={galleryRef} />
            </div>
        </section>
    );
});

const NewsItem = ({ item, background, t }) => {
    const contentRef = useRef(null);
    const [isOverflowed, setIsOverflowed] = useState(false);
    const navigate = useNavigate();

    const formatDate = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return '';

        const localeMap = {
            'ua': 'uk',
            'en': 'en',
            'ko': 'ko',
            'fr': 'fr',
            'ru': 'ru',
        };
        const locale = localeMap[i18n.language] || 'en';

        return date.toLocaleString(locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const getPluralForm = (count, singular, few, many) => {
        const mod10 = count % 10;
        const mod100 = count % 100;

        if (mod10 === 1 && mod100 !== 11) {
            return singular;
        }
        if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
            return few;
        }
        return many;
    };

    const calculateReadTime = (text) => {
        if (!text) return `${t('news.readTime', { count: 1 })}`;

        const words = text.split(/\s+/).length;
        const readTime = Math.ceil(words / 200);

        const singular = t('news.readTimeSingular');
        const few = t('news.readTimeFew');
        const many = t('news.readTimeMany');

        const pluralForm = getPluralForm(readTime, singular, few, many);

        return `${readTime} ${pluralForm} ${t('news.forReading')}`;
    };

    useEffect(() => {
        const current = contentRef.current;
        if (current) {
            const observer = new ResizeObserver(() => {
                setIsOverflowed(current.scrollHeight > current.clientHeight);
            });
            observer.observe(current);
            return () => {
                observer.disconnect();
            };
        }
    }, [item.description]);

    useLayoutEffect(() => {
        const updateHeight = () => {
            const newsItems = document.querySelectorAll('.news-item');
            newsItems.forEach((newsItem) => {
                const thumbnail = newsItem.querySelector('.news-thumbnail');
                const newsData = newsItem.querySelector('.news-data');

                if (thumbnail && newsData) {
                    const style = window.getComputedStyle(thumbnail);
                    if (style.display === 'none') {
                        newsData.style.maxHeight = '';
                    } else {
                        const thumbnailHeight = thumbnail.offsetHeight;
                        if (thumbnailHeight > 0) {
                            newsData.style.maxHeight = `${thumbnailHeight}px`;
                        } else {
                            newsData.style.maxHeight = '';
                        }
                    }
                }
            });
        };

        requestAnimationFrame(updateHeight);

        const onResize = () => {
            requestAnimationFrame(updateHeight);
        };
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    const readMore = () => {
        navigate(`/news/${item.id}`);
    };

    return (
        <article className="news-item">
            <div className="news-content">
                <div className="news-thumbnail">
                    <img
                        src={background}
                        alt="news thumbnail"/>
                </div>
                <div className="news-data">
                    <div className="news-meta">
                        <span>
                            <FontAwesomeIcon icon={faCalendarAlt}/> {formatDate(item.created_at)}
                        </span>
                        {/*<span>*/}
                        {/*    <FontAwesomeIcon icon={faUser}/> {item.author || 'admin'}*/}
                        {/*</span>*/}
                        <span>
                            <FontAwesomeIcon icon={faClock}/> {calculateReadTime(item.description)}
                        </span>
                        {isOverflowed && (
                            <span className="read-more" onClick={readMore}>
                                <FontAwesomeIcon className="" icon={faBookOpen}/> {t('news.readMore') || 'Read More'}
                                <FontAwesomeIcon className="fa-arrow-right" icon={faArrowRight}/>
                            </span>
                        )}
                    </div>
                    <span className="news-title">
                        <Link to={`/news/${item.id}`}>{item.title}</Link>
                    </span>
                    <div
                        ref={contentRef}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(item.description, {FORBID_TAGS: ['img']})
                        }}
                        className="news-description"
                    ></div>
                </div>
            </div>
        </article>
    );
};

export default News;
